import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Grid,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  IconButton,
} from '@mui/material';
import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import {
  fetchSiteGroupReport,
  selectCurrentSiteGroupReport,
  selectCurrentSiteGroupReportLoading,
  selectCurrentSiteGroupReportError,
} from '@sophocles/features/reports/siteGroupOperationalReportsSlice';

/**
 * SiteGroupDashboard component displays operational compliance for a specific site group
 * Shows individual sites with their 30/60/90 day pass rates
 */
const SiteGroupDashboard = () => {
  const { t } = useTranslation();
  const { cust_id, sitegroup_id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useUser();

  const report = useSelector(selectCurrentSiteGroupReport);
  const loading = useSelector(selectCurrentSiteGroupReportLoading);
  const error = useSelector(selectCurrentSiteGroupReportError);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Expandable rows state
  const [expandedRows, setExpandedRows] = useState({});

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset to first page when searching
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch data when params change
  useEffect(() => {
    if (sitegroup_id) {
      const offset = (page - 1) * pageSize;
      dispatch(
        fetchSiteGroupReport({
          id: sitegroup_id,
          params: {
            limit: pageSize,
            offset: offset,
            search: debouncedSearch,
          },
        }),
      );
    }
  }, [dispatch, sitegroup_id, page, pageSize, debouncedSearch]);

  const handleRefresh = () => {
    if (sitegroup_id) {
      const offset = (page - 1) * pageSize;
      dispatch(
        fetchSiteGroupReport({
          id: sitegroup_id,
          params: {
            limit: pageSize,
            offset: offset,
            search: debouncedSearch,
          },
        }),
      );
    }
  };

  const handleSiteClick = siteId => {
    // Navigate to site compliance view with proper customer/plant context
    navigate(`/c/${cust_id}/plant/${sitegroup_id}/structure/${siteId}/compliance`);
  };

  const handleToggleRow = siteId => {
    setExpandedRows(prev => ({
      ...prev,
      [siteId]: !prev[siteId],
    }));
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = event => {
    setPageSize(event.target.value);
    setPage(1); // Reset to first page when changing page size
  };

  const handleSearchChange = event => {
    setSearchTerm(event.target.value);
  };

  // Helper to format percentage
  const formatPercent = value => {
    if (value === null || value === undefined) return 'N/A';
    return `${value.toFixed(1)}%`;
  };

  // Helper to get color based on pass rate
  const getPassRateColor = passRate => {
    if (passRate === null || passRate === undefined) return 'text.secondary';
    if (passRate >= 90) return 'success.main';
    if (passRate >= 75) return 'warning.main';
    return 'error.main';
  };

  // Helper to render stats cell with N/A handling and pending count for staff
  const renderStatsCell = stats => {
    if (!stats) {
      return (
        <TableCell align="center">
          <Typography variant="body2" color="text.secondary">
            N/A
          </Typography>
        </TableCell>
      );
    }

    return (
      <TableCell align="center">
        <Box display="flex" flexDirection="column" alignItems="center">
          <Typography color={getPassRateColor(stats.pass_rate)} variant="body2" fontWeight="medium">
            {formatPercent(stats.pass_rate)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {stats.passed}/{stats.total}
            {user?.isStaff && stats.pending > 0 && (
              <Typography component="span" sx={{ marginLeft: '4px', color: 'warning.main' }}>
                ({stats.pending} pending)
              </Typography>
            )}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={stats.pass_rate || 0}
            sx={{ width: 60, mt: 0.5, height: 4, borderRadius: 2 }}
            color={(stats.pass_rate || 0) >= 90 ? 'success' : (stats.pass_rate || 0) >= 75 ? 'warning' : 'error'}
          />
        </Box>
      </TableCell>
    );
  };

  if (loading && !report) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3, bgcolor: 'error.light', border: 1, borderColor: 'error.main' }}>
          <Typography variant="h6" sx={{ color: 'error.main', mb: 1 }}>
            {t('siteGroupDashboard.errorLoadingReports', { defaultValue: 'Error loading reports' })}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.primary' }}>
            {error}
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (!report) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">
          {t('siteGroupDashboard.noDataAvailable', { defaultValue: 'No data available' })}
        </Typography>
      </Box>
    );
  }

  const sites = report.sites || [];
  const summaryStats = report.summary_stats || {};

  // Calculate the age of reporting (days between oldest and newest report across all sites)
  let oldestReportDate = null;
  let newestReportDate = null;

  sites.forEach(site => {
    if (site.recent_reports && site.recent_reports.length > 0) {
      site.recent_reports.forEach(report => {
        const reportDate = new Date(report.report_date);
        if (!oldestReportDate || reportDate < oldestReportDate) {
          oldestReportDate = reportDate;
        }
        if (!newestReportDate || reportDate > newestReportDate) {
          newestReportDate = reportDate;
        }
      });
    }
  });

  // Calculate reporting age in days
  const reportingAgeDays =
    oldestReportDate && newestReportDate ? Math.ceil((newestReportDate - oldestReportDate) / (1000 * 60 * 60 * 24)) : 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4">
          {report.site_group_name} - {t('operationalReports.compliance', { defaultValue: 'Operational Compliance' })}
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={handleRefresh} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                {t('siteGroupDashboard.totalSites', { defaultValue: 'Total Sites' })}
              </Typography>
              <Typography variant="h4">{summaryStats.total_sites || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                {t('siteGroupDashboard.totalReports30Days', { defaultValue: 'Reports (30 Days)' })}
              </Typography>
              <Typography variant="h4">{summaryStats.total_reports_30_days || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                {t('siteGroupDashboard.passed30Days', { defaultValue: 'Passed (30 Days)' })}
              </Typography>
              <Typography color="success.main" variant="h4">
                {summaryStats.passed_30_days || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                {t('siteGroupDashboard.passRate30Days', { defaultValue: '30-Day Pass Rate' })}
              </Typography>
              <Typography color={getPassRateColor(summaryStats.pass_rate_30_days)} variant="h4">
                {formatPercent(summaryStats.pass_rate_30_days)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Controls */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <TextField
          placeholder={t('general.search', { defaultValue: 'Search sites...' })}
          value={searchTerm}
          onChange={handleSearchChange}
          size="small"
          hiddenLabel
          sx={{ minWidth: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary">
            {t('general.rowsPerPage', { defaultValue: 'Rows per page:' })}
          </Typography>
          <Select value={pageSize} onChange={handlePageSizeChange} size="small" hiddenLabel sx={{ minWidth: 80 }}>
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </Box>
      </Box>

      {/* Sites Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 50 }} />
              <TableCell>{t('general.name', { defaultValue: 'Name' })}</TableCell>
              <TableCell align="center">{t('siteGroupDashboard.passRate30', { defaultValue: '30 Days' })}</TableCell>
              <TableCell align="center">{t('siteGroupDashboard.passRate60', { defaultValue: '60 Days' })}</TableCell>
              <TableCell align="center">{t('siteGroupDashboard.passRate90', { defaultValue: '90 Days' })}</TableCell>
              <TableCell align="center">{t('siteGroupDashboard.recentReports', { defaultValue: 'Recent' })}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sites.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                  <Typography color="text.secondary">
                    {t('siteGroupDashboard.noSitesFound', { defaultValue: 'No sites found' })}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sites.map(site => (
                <React.Fragment key={site.site_id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={e => {
                          e.stopPropagation();
                          handleToggleRow(site.site_id);
                        }}
                      >
                        {expandedRows[site.site_id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ cursor: 'pointer' }} onClick={() => handleSiteClick(site.site_id)}>
                      <Typography variant="body1" fontWeight="medium">
                        {site.site_name}
                      </Typography>
                    </TableCell>
                    {renderStatsCell(site.stats_30_days)}
                    {renderStatsCell(site.stats_60_days)}
                    {renderStatsCell(site.stats_90_days)}
                    <TableCell align="center">
                      <Typography variant="body2">{site.recent_reports?.length || 0}</Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                      <Collapse in={expandedRows[site.site_id]} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                          <Typography variant="h6" gutterBottom component="div">
                            {t('siteGroupDashboard.latestReport', { defaultValue: 'Latest Report' })}
                          </Typography>
                          {site.recent_reports && site.recent_reports.length > 0 ? (
                            (() => {
                              const latestReport = site.recent_reports[0];
                              const items = latestReport.items || [];
                              const totalItems = items.length;
                              const passedItems = items.filter(item => item.status === 'passed').length;
                              const failedItems = items.filter(item => item.status === 'failed').length;
                              const passRate = totalItems > 0 ? (passedItems / totalItems) * 100 : null;

                              return (
                                <Card sx={{ maxWidth: 600 }}>
                                  <CardContent>
                                    <Grid container spacing={2}>
                                      <Grid size={6}>
                                        <Typography variant="caption" color="text.secondary">
                                          {t('operationalReports.reportDate', { defaultValue: 'Report Date' })}
                                        </Typography>
                                        <Typography variant="body2">
                                          {new Date(latestReport.report_date).toLocaleDateString()}
                                        </Typography>
                                      </Grid>
                                      <Grid size={6}>
                                        <Typography variant="caption" color="text.secondary">
                                          {t('operationalReports.status', { defaultValue: 'Status' })}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color:
                                              latestReport.status === 'passed'
                                                ? 'success.main'
                                                : latestReport.status === 'failed'
                                                  ? 'error.main'
                                                  : latestReport.status === 'pending'
                                                    ? 'warning.main'
                                                    : 'text.secondary',
                                            textTransform: 'capitalize',
                                            fontWeight: 'medium',
                                          }}
                                        >
                                          {latestReport.status}
                                        </Typography>
                                      </Grid>
                                      <Grid size={6}>
                                        <Typography variant="caption" color="text.secondary">
                                          {t('operationalReports.compliance', { defaultValue: 'Compliance' })}
                                        </Typography>
                                        <Typography variant="body2">
                                          {totalItems > 0
                                            ? `${passedItems}/${totalItems} (${formatPercent(passRate)})`
                                            : 'N/A'}
                                        </Typography>
                                      </Grid>
                                      <Grid size={6}>
                                        <Typography variant="caption" color="text.secondary">
                                          {t('operationalReports.failed', { defaultValue: 'Failed Items' })}
                                        </Typography>
                                        <Typography variant="body2" color="error.main">
                                          {totalItems > 0 ? failedItems : 'N/A'}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                  </CardContent>
                                </Card>
                              );
                            })()
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {t('siteGroupDashboard.noReportsAvailable', { defaultValue: 'No reports available' })}
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {report?.pagination && report.pagination.count > 0 && (
        <Box display="flex" justifyContent="center" alignItems="center" mt={3}>
          <Pagination
            count={Math.ceil(report.pagination.count / pageSize)}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            {t('general.showing', { defaultValue: 'Showing' })} {report.pagination.offset + 1}-
            {Math.min(report.pagination.offset + pageSize, report.pagination.count)}{' '}
            {t('general.of', { defaultValue: 'of' })} {report.pagination.count}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SiteGroupDashboard;
